import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { FC, MouseEvent, useEffect, useMemo, useState } from "react";
import {
  createService,
  deleteServiceByID,
  Service,
  ServiceStatus,
  updateServiceStatus,
  updateServiceStatusPayload,
} from "src/apis/services";
import { getUserId } from "src/utils/auth";
import { formatDateToBR } from "src/utils/date";
import NoDataAlert from "src/pages/components/NoDataAlert";
import { useNavigate } from "react-router-dom";

type PsychologistServicesTableProps = {
  services: Service[];
  setError?: (err: string) => void;
  onStatusUpdated?: () => void;
};

type ActionType = "Realizado" | "Paciente Faltou" | "cancelada";

const ACTIONS: Array<{ value: ActionType; label: string }> = [
  { value: "Realizado", label: "Realizado" },
  { value: "Paciente Faltou", label: "Paciente Faltou" },
  { value: "cancelada", label: "Consulta cancelada" },
];

const formatValue = (value?: string | null): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue || normalizedValue.toLowerCase() === "null") {
    return "-";
  }

  return normalizedValue;
};

const PsychologistServicesTable: FC<PsychologistServicesTableProps> = ({
  services,
  setError = (ignored: string) => {},
  onStatusUpdated,
}) => {
  const [entries, setEntries] = useState<Service[]>(services);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    setEntries(services);
  }, [services]);

  const hasServices = useMemo(
    () => Array.isArray(entries) && entries.length > 0,
    [entries]
  );

  const currentPsychologistId = useMemo(() => getUserId(), []);

  const canExecuteAction = (service: Service): boolean =>
    Boolean(service.service_id || service.schedule_id);

  const handleOpenActionMenu = (
    event: MouseEvent<HTMLButtonElement>,
    service: Service
  ) => {
    event.stopPropagation();
    setSelectedService(service);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
    setSelectedService(null);
  };

  const handleAction = async (action: ActionType) => {
    if (!selectedService) {
      return;
    }

    if (!currentPsychologistId) {
      setError("ID do psicólogo não encontrado.");
      handleCloseActionMenu();
      return;
    }

    if (!selectedService.service_id && !selectedService.schedule_id) {
      setError("Não há atendimento nem agendamento disponível para atualizar este registro.");
      handleCloseActionMenu();
      return;
    }

    setActionInProgress(true);

    try {
      if (selectedService.service_id) {
        if (action === "cancelada") {
          await updateServiceStatusPayload(
            currentPsychologistId,
            selectedService.service_id,
            { status_consulta: "cancelada" }
          );
          setEntries((currentEntries) =>
            currentEntries.map((entry) =>
              entry.service_id === selectedService.service_id
                ? {
                    ...entry,
                    status_consulta: "cancelada",
                    service_status: entry.service_status,
                  }
                : entry
            )
          );
        } else {
          await updateServiceStatus(
            currentPsychologistId,
            selectedService.service_id,
            action as ServiceStatus
          );
          setEntries((currentEntries) =>
            currentEntries.map((entry) =>
              entry.service_id === selectedService.service_id
                ? {
                    ...entry,
                    service_status: action,
                    status_consulta: undefined,
                  }
                : entry
            )
          );
        }
      } else if (selectedService.schedule_id) {
        const payload = {
          schedule_id: selectedService.schedule_id,
          collaborator_id: selectedService.id_colaborador,
          company_id: selectedService.id_empresa,
          date_of_service: selectedService.atendimento,
          area: selectedService.area || "",
          city: selectedService.cidade || "",
          annotations: selectedService.observacoes || "",
          complaint: selectedService.queixa_inicial || "",
          ...(action === "cancelada"
            ? { status_consulta: "cancelada" }
            : { service_status: action }),
        };

        const created = await createService(payload);

        if (created?.id) {
          setEntries((currentEntries) =>
            currentEntries.map((entry) =>
              entry.schedule_id === selectedService.schedule_id
                ? {
                    ...entry,
                    service_id: Number(created.id),
                    id_atendimento: Number(created.id) || entry.id_atendimento,
                    status_consulta:
                      action === "cancelada" ? "cancelada" : entry.status_consulta,
                    service_status:
                      action === "cancelada" ? entry.service_status : action,
                  }
                : entry
            )
          );
        }
      }

      setError("");
      onStatusUpdated?.();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível atualizar o status do atendimento."
      );
    } finally {
      setActionInProgress(false);
      handleCloseActionMenu();
    }
  };

  const handleDelete = async (service: Service) => {
    if (!service.service_id || !currentPsychologistId) return;

    const confirmed = window.confirm(
      `Deseja excluir o atendimento de ${formatDateToBR(service.atendimento)}?`
    );
    if (!confirmed) return;

    const reason = window.prompt("Informe o motivo da exclusão:") ?? "";

    try {
      await deleteServiceByID(currentPsychologistId, service.service_id, reason);
      setEntries((current) =>
        current.filter((e) => e.service_id !== service.service_id)
      );
      setError("");
      onStatusUpdated?.();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Não foi possível excluir o atendimento."
      );
    }
  };

  const renderCalendarStatus = (service: Service) => {
    if (service.status_consulta === "marcada" && !service.service_status) {
      return <Chip label="Marcada" color="warning" size="small" />;
    }

    if (service.status_consulta === "cancelada") {
      return <Chip label="Cancelada" color="error" size="small" />;
    }

    if (service.status_consulta) {
      return <Chip label={formatValue(service.status_consulta)} size="small" />;
    }

    return "-";
  };

  return (
    <>
      {hasServices ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Data de atendimento</strong></TableCell>
                <TableCell><strong>Status da sessão</strong></TableCell>
                <TableCell><strong>Status do calendário</strong></TableCell>
                <TableCell><strong>Colaborador</strong></TableCell>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Cidade</strong></TableCell>
                <TableCell><strong>Observações</strong></TableCell>
                <TableCell><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((service, index) => {
                const isMarkedConsultation =
                  service.status_consulta === "marcada" && !service.service_status;
                const rowKey =
                  service.service_id || service.schedule_id || service.id_atendimento || index;

                return (
                  <TableRow
                    hover
                    key={rowKey}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: isMarkedConsultation
                        ? "action.disabledBackground"
                        : undefined,
                    }}
                  >
                    <TableCell>{formatDateToBR(service.atendimento)}</TableCell>
                    <TableCell>{formatValue(service.service_status)}</TableCell>
                    <TableCell>{renderCalendarStatus(service)}</TableCell>
                    <TableCell>{formatValue(service.colaborador)}</TableCell>
                    <TableCell>{formatValue(service.empresa)}</TableCell>
                    <TableCell>{formatValue(service.cidade)}</TableCell>
                    <TableCell>{formatValue(service.observacoes)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Alterar status do atendimento">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(event) => handleOpenActionMenu(event, service)}
                              disabled={!canExecuteAction(service)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {Boolean(service.service_id) && (
                          <>
                            <Tooltip title="Editar atendimento">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/atendimentos/${service.service_id}/editar`);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir atendimento">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(service);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoDataAlert />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseActionMenu}
      >
        {ACTIONS.map((action) => (
          <MenuItem
            key={action.value}
            disabled={actionInProgress}
            onClick={() => handleAction(action.value)}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PsychologistServicesTable;
